import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    ShieldCheckIcon,
    SignalIcon,
    EnvelopeIcon,
    LockClosedIcon,
    BugAntIcon,
    SunIcon,
    MoonIcon,
    GlobeAltIcon,
    ChartBarIcon,
    TicketIcon,
    ChatBubbleLeftRightIcon,
    BellIcon,
    UserCircleIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    CommandLineIcon,
    ChevronRightIcon,
    ClockIcon,
    Cog6ToothIcon,
    ArrowRightStartOnRectangleIcon,
    QuestionMarkCircleIcon,
    XMarkIcon,
    BoltIcon,
    WifiIcon,
    CpuChipIcon,
    HomeIcon,
    ArrowPathIcon,
    InformationCircleIcon,
    KeyIcon,
    ServerIcon,
    FireIcon,
    EyeIcon,
    ArchiveBoxIcon,
    ArchiveBoxArrowDownIcon,
    BriefcaseIcon,
} from "@heroicons/react/20/solid";
import { SidebarLayout } from "src/components/base/sidebar-layout";
import {
    Sidebar,
    SidebarHeader,
    SidebarBody,
    SidebarSection,
    SidebarItem,
    SidebarLabel,
    SidebarHeading,
    SidebarDivider,
    SidebarSpacer,
} from "src/components/base/sidebar";
import { Button } from "src/components/base/button";
import { Navbar, NavbarSection, NavbarSpacer } from "src/components/base/navbar";
import type { View, ViewContext } from "./landing/types";
import type { UnifiedEvent } from "./landing/data/events";
import { UNIFIED_EVENTS } from "./landing/data/events";
import { SearchBar } from "./landing/SearchBar";
import { InvestigatePanel } from "./landing/InvestigatePanel";
import { OverviewDashboard } from "./landing/views/OverviewDashboard";
import { SocDashboard } from "./landing/views/SocDashboard";
import { NdrDashboard } from "./landing/views/NdrDashboard";
import { MailDashboard } from "./landing/views/MailDashboard";
import { ArchiveDashboard } from "./landing/views/ArchiveDashboard";
import { ContractDashboard } from "./landing/views/ContractDashboard";
import { DownloadsDashboard } from "./landing/views/DownloadsDashboard";
import { ZtnaDashboard } from "./landing/views/ZtnaDashboard";
import { PhishingDashboard } from "./landing/views/PhishingDashboard";
import { PentestsDashboard } from "./landing/views/PentestsDashboard";
import { SocAlertDetailView } from "./landing/views/SocAlertDetailView";
import { PentestFindingDetailView } from "./landing/views/PentestFindingDetailView";
import { TicketListView } from "./landing/views/TicketListView";
import { TicketDetailView } from "./landing/views/TicketDetailView";
import { ChatView } from "./landing/views/ChatView";
import { SettingsNotifications } from "./landing/views/SettingsNotifications";
import { SettingsSocExceptions } from "./landing/views/SettingsSocExceptions";
import { SettingsProfile } from "./landing/views/SettingsProfile";
import { SoftwareInventory } from "./landing/views/SoftwareInventory";

/* ──────────────────────────────────────────────────────────────────────────────
   TYPES & CONSTANTS
   ────────────────────────────────────────────────────────────────────────────── */

interface Notification {
    id: string;
    icon: React.ReactNode;
    title: string;
    body: string;
    time: string;
    severity: "critical" | "warning" | "info" | "success";
    read: boolean;
}

interface CommandItem {
    id: string;
    label: string;
    category: "Navigation" | "Events" | "Actions";
    icon: React.ReactNode;
    action: () => void;
    keywords?: string;
}

const VIEW_DISPLAY_NAMES: Record<View, string> = {
    overview: "Overview",
    soc: "SOC",
    ndr: "NDR",
    mail: "Mail Gateway",
    downloads: "Downloads",
    archive: "Archive",
    ztna: "ZTNA",
    phishing: "Phishing Simulation",
    pentests: "Pentests",
    contracts: "Contracts",
    "soc-alert-detail": "SOC Alert Detail",
    "pentest-finding-detail": "Pentest Finding",
    tickets: "Tickets",
    "ticket-detail": "Ticket Detail",
    chat: "Live Chat",
    "settings-notifications": "Notification Settings",
    "settings-approved-software": "Approved Software",
    "settings-soc-exceptions": "SOC Exceptions",
    "settings-profile": "Profile",
};

const VIEW_HIERARCHY: Record<View, { parent?: View; label: string }> = {
    overview: { label: "Overview" },
    soc: { label: "SOC" },
    ndr: { label: "NDR" },
    mail: { label: "Mail Gateway" },
    downloads: { label: "Downloads" },
    archive: { label: "Archive" },
    contracts: { label: "Contracts" },
    ztna: { label: "ZTNA" },
    phishing: { label: "Phishing" },
    pentests: { label: "Pentests" },
    "soc-alert-detail": { parent: "soc", label: "Alert Detail" },
    "pentest-finding-detail": { parent: "pentests", label: "Finding Detail" },
    tickets: { label: "Tickets" },
    "ticket-detail": { parent: "tickets", label: "Ticket Detail" },
    chat: { label: "Live Chat" },
    "settings-notifications": { label: "Notifications" },
    "settings-soc-exceptions": { label: "SOC Exceptions" },
    "settings-approved-software": { label: "Approved Software" },
    "settings-profile": { label: "Profile" },
};

const SEVERITY_STYLES = {
    critical: "bg-red-500/20 text-red-400 border-red-500/30",
    warning: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    success: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
};

const INITIAL_NOTIFICATIONS: Notification[] = [
    {
        id: "n1",
        icon: <FireIcon className="h-5 w-5 text-red-400" />,
        title: "Critical: Brute Force Attack Detected",
        body: "SOC-1247 — 185.143.223.1 targeting fw-prod-01. Automated response engaged.",
        time: "2 min ago",
        severity: "critical",
        read: false,
    },
    {
        id: "n2",
        icon: <ShieldCheckIcon className="h-5 w-5 text-amber-400" />,
        title: "Malware Quarantined",
        body: "Mail Gateway blocked malicious attachment from fak3-vendor.com. 3 users affected.",
        time: "8 min ago",
        severity: "warning",
        read: false,
    },
    {
        id: "n3",
        icon: <BoltIcon className="h-5 w-5 text-blue-400" />,
        title: "NDR Policy Updated",
        body: "C2 domain 91.215.85.12 added to global blocklist across all segments.",
        time: "15 min ago",
        severity: "info",
        read: false,
    },
    {
        id: "n4",
        icon: <TicketIcon className="h-5 w-5 text-emerald-400" />,
        title: "Ticket TKT-2847 Resolved",
        body: "VPN connectivity issue for Munich office has been resolved.",
        time: "23 min ago",
        severity: "success",
        read: false,
    },
    {
        id: "n5",
        icon: <LockClosedIcon className="h-5 w-5 text-amber-400" />,
        title: "ZTNA Session Terminated",
        body: "User s.braun session denied — trust score dropped below threshold.",
        time: "31 min ago",
        severity: "warning",
        read: true,
    },
    {
        id: "n6",
        icon: <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />,
        title: "Pentest Finding: OpenSSL CVE",
        body: "PT-004 critical finding on lb-prod-02 — patch deployment recommended.",
        time: "1 hr ago",
        severity: "critical",
        read: true,
    },
    {
        id: "n7",
        icon: <ServerIcon className="h-5 w-5 text-blue-400" />,
        title: "System Maintenance Scheduled",
        body: "Database maintenance window: tonight 02:00-04:00 CET.",
        time: "2 hr ago",
        severity: "info",
        read: true,
    },
];

const VIEW_SHORTCUTS: { key: string; view: View }[] = [
    { key: "1", view: "overview" },
    { key: "2", view: "soc" },
    { key: "3", view: "ndr" },
    { key: "4", view: "mail" },
    { key: "5", view: "ztna" },
    { key: "6", view: "tickets" },
    { key: "7", view: "chat" },
];

/* ──────────────────────────────────────────────────────────────────────────────
   LIVE CLOCK COMPONENT
   ────────────────────────────────────────────────────────────────────────────── */

function LiveClock() {
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    const time = now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const date = now.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

    return (
        <div className="hidden items-center gap-2 lg:flex">
            <ClockIcon className="h-4 w-4 text-zinc-400 dark:text-zinc-500" />
            <div className="flex flex-col items-end">
                <span className="font-mono text-xs font-semibold tabular-nums tracking-wider text-zinc-700 dark:text-zinc-300">
                    {time}
                </span>
                <span className="text-[10px] text-zinc-400 dark:text-zinc-500">{date}</span>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────────────
   NOTIFICATION BELL COMPONENT
   ────────────────────────────────────────────────────────────────────────────── */

function NotificationBell() {
    const [notifications, setNotifications] = useState(INITIAL_NOTIFICATIONS);
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const markAllRead = useCallback(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }, []);

    const markRead = useCallback((id: string) => {
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    }, []);

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="relative rounded-xl p-2 text-zinc-500 transition-all duration-200 hover:bg-zinc-100 hover:text-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            >
                <BellIcon className="h-5 w-5" />
                {unreadCount > 0 && (
                    <span className="h-4.5 min-w-4.5 absolute -right-0.5 -top-0.5 flex items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-rose-600 px-1 text-[10px] font-bold text-white shadow-lg shadow-red-500/30 ring-2 ring-white dark:ring-zinc-900">
                        {unreadCount}
                    </span>
                )}
                {/* Pulse ring animation */}
                {unreadCount > 0 && (
                    <span className="h-4.5 w-4.5 absolute -right-0.5 -top-0.5 animate-ping rounded-full bg-red-400 opacity-40" />
                )}
            </button>

            {/* Notification dropdown */}
            <div
                className={`absolute right-0 top-full z-50 mt-2 w-96 origin-top-right transition-all duration-300 ${
                    open
                        ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                        : "pointer-events-none -translate-y-2 scale-95 opacity-0"
                }`}
            >
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/80 shadow-2xl shadow-black/20 backdrop-blur-2xl dark:border-zinc-700/50 dark:bg-zinc-900/90">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-zinc-200/50 px-5 py-3.5 dark:border-zinc-700/50">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Notifications</h3>
                            {unreadCount > 0 && (
                                <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-bold text-red-500">
                                    {unreadCount} new
                                </span>
                            )}
                        </div>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllRead}
                                className="text-xs font-medium text-blue-500 transition-colors hover:text-blue-400"
                            >
                                Mark all as read
                            </button>
                        )}
                    </div>

                    {/* Notification list */}
                    <div className="max-h-[420px] overflow-y-auto">
                        {notifications.map((n, i) => (
                            <button
                                key={n.id}
                                onClick={() => markRead(n.id)}
                                className={`flex w-full gap-3 border-b border-zinc-100/50 px-5 py-3.5 text-left transition-all duration-200 hover:bg-zinc-50/80 dark:border-zinc-800/50 dark:hover:bg-zinc-800/50 ${
                                    !n.read ? "bg-blue-50/30 dark:bg-blue-500/5" : ""
                                }`}
                                style={{ animationDelay: `${i * 40}ms` }}
                            >
                                {/* Unread dot */}
                                <div className="flex flex-col items-center pt-1">
                                    <div
                                        className={`h-2 w-2 rounded-full transition-all duration-300 ${
                                            n.read
                                                ? "scale-0 bg-transparent"
                                                : "scale-100 bg-blue-500 shadow-lg shadow-blue-500/50"
                                        }`}
                                    />
                                </div>
                                {/* Icon */}
                                <div className="flex-shrink-0 pt-0.5">{n.icon}</div>
                                {/* Content */}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`truncate text-xs font-semibold ${n.read ? "text-zinc-600 dark:text-zinc-400" : "text-zinc-900 dark:text-white"}`}
                                        >
                                            {n.title}
                                        </span>
                                        <span
                                            className={`flex-shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${SEVERITY_STYLES[n.severity]}`}
                                        >
                                            {n.severity}
                                        </span>
                                    </div>
                                    <p className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-500">
                                        {n.body}
                                    </p>
                                    <span className="mt-1 block text-[10px] text-zinc-400 dark:text-zinc-600">
                                        {n.time}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-zinc-200/50 px-5 py-2.5 dark:border-zinc-700/50">
                        <button className="w-full text-center text-xs font-medium text-blue-500 transition-colors hover:text-blue-400">
                            View all notifications
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────────────
   USER AVATAR MENU COMPONENT
   ────────────────────────────────────────────────────────────────────────────── */

function UserAvatarMenu({ onNavigate }: { onNavigate: (v: View) => void }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const menuItems = [
        {
            icon: <UserCircleIcon className="h-4 w-4" />,
            label: "Profile",
            action: () => {
                onNavigate("settings-profile");
                setOpen(false);
            },
        },
        {
            icon: <Cog6ToothIcon className="h-4 w-4" />,
            label: "Settings",
            action: () => {
                onNavigate("settings-notifications");
                setOpen(false);
            },
        },
        {
            icon: <KeyIcon className="h-4 w-4" />,
            label: "Keyboard Shortcuts",
            action: () => {
                setOpen(false);
            },
        },
        { divider: true },
        {
            icon: <ArrowRightStartOnRectangleIcon className="h-4 w-4 text-red-400" />,
            label: "Sign Out",
            action: () => setOpen(false),
            danger: true,
        },
    ] as const;

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="group relative flex items-center gap-2 rounded-xl p-1 transition-all duration-200 hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
                {/* Avatar */}
                <div className="relative">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-xs font-bold text-white shadow-lg shadow-violet-500/25 ring-2 ring-white/80 transition-shadow group-hover:shadow-violet-500/40 dark:ring-zinc-800">
                        MM
                    </div>
                    {/* Online indicator */}
                    <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500 shadow-lg shadow-emerald-500/50 dark:border-zinc-900" />
                </div>
                <div className="hidden flex-col items-start xl:flex">
                    <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">Max Mustermann</span>
                    <span className="text-[10px] text-zinc-400 dark:text-zinc-500">Security Analyst</span>
                </div>
            </button>

            {/* Dropdown */}
            <div
                className={`absolute right-0 top-full z-50 mt-2 w-56 origin-top-right transition-all duration-300 ${
                    open
                        ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                        : "pointer-events-none -translate-y-2 scale-95 opacity-0"
                }`}
            >
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/80 shadow-2xl shadow-black/20 backdrop-blur-2xl dark:border-zinc-700/50 dark:bg-zinc-900/90">
                    {/* User info header */}
                    <div className="border-b border-zinc-200/50 px-4 py-3 dark:border-zinc-700/50">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-sm font-bold text-white">
                                MM
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-zinc-900 dark:text-white">
                                    Max Mustermann
                                </div>
                                <div className="text-[11px] text-zinc-500">max@trufflepig.com</div>
                            </div>
                        </div>
                    </div>
                    <div className="py-1.5">
                        {menuItems.map((item, i) =>
                            "divider" in item ? (
                                <div key={i} className="my-1 border-t border-zinc-200/50 dark:border-zinc-700/50" />
                            ) : (
                                <button
                                    key={i}
                                    onClick={item.action}
                                    className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                                        "danger" in item && item.danger
                                            ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                                            : "text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800"
                                    }`}
                                >
                                    {item.icon}
                                    <span>{item.label}</span>
                                </button>
                            ),
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────────────
   BREADCRUMBS COMPONENT
   ────────────────────────────────────────────────────────────────────────────── */

function Breadcrumbs({ viewCtx, onNavigate }: { viewCtx: ViewContext; onNavigate: (v: View) => void }) {
    const crumbs: { label: string; view?: View }[] = [{ label: "Platform" }];

    // Build chain from hierarchy
    const buildChain = (v: View): { label: string; view: View }[] => {
        const entry = VIEW_HIERARCHY[v];
        if (entry.parent) {
            return [...buildChain(entry.parent), { label: entry.label, view: v }];
        }
        return [{ label: entry.label, view: v }];
    };

    const chain = buildChain(viewCtx.view);
    crumbs.push(...chain);

    // If there's a detail ID, add it
    if (viewCtx.detailId) {
        crumbs.push({ label: viewCtx.detailId });
    }

    return (
        <div className="flex items-center gap-1.5 overflow-x-auto px-1 py-2">
            <HomeIcon className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400 dark:text-zinc-500" />
            {crumbs.map((crumb, i) => (
                <div key={i} className="flex items-center gap-1.5">
                    {i > 0 && <ChevronRightIcon className="h-3 w-3 flex-shrink-0 text-zinc-300 dark:text-zinc-600" />}
                    {crumb.view && i < crumbs.length - 1 ? (
                        <button
                            onClick={() => crumb.view && onNavigate(crumb.view)}
                            className="whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium text-zinc-500 transition-all duration-150 hover:bg-zinc-100 hover:text-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                        >
                            {crumb.label}
                        </button>
                    ) : (
                        <span
                            className={`whitespace-nowrap px-1.5 py-0.5 text-xs font-medium ${
                                i === crumbs.length - 1
                                    ? "text-zinc-900 dark:text-white"
                                    : "text-zinc-400 dark:text-zinc-500"
                            }`}
                        >
                            {crumb.label}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────────────
   COMMAND PALETTE COMPONENT
   ────────────────────────────────────────────────────────────────────────────── */

function CommandPalette({
    open,
    onClose,
    onNavigate,
    onInvestigate,
    recentViews,
}: {
    open: boolean;
    onClose: () => void;
    onNavigate: (v: View) => void;
    onInvestigate: (e: UnifiedEvent) => void;
    recentViews: View[];
}) {
    const [query, setQuery] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    // Build command items
    const allItems = useMemo((): CommandItem[] => {
        const nav: CommandItem[] = [
            {
                id: "nav-overview",
                label: "Overview Dashboard",
                category: "Navigation",
                icon: <ChartBarIcon className="h-4 w-4" />,
                action: () => onNavigate("overview"),
                keywords: "home dashboard overview",
            },
            {
                id: "nav-soc",
                label: "SOC Dashboard",
                category: "Navigation",
                icon: <ShieldCheckIcon className="h-4 w-4" />,
                action: () => onNavigate("soc"),
                keywords: "security operations center soc alerts",
            },
            {
                id: "nav-ndr",
                label: "NDR Dashboard",
                category: "Navigation",
                icon: <SignalIcon className="h-4 w-4" />,
                action: () => onNavigate("ndr"),
                keywords: "network detection response ndr",
            },
            {
                id: "nav-mail",
                label: "Mail Gateway",
                category: "Navigation",
                icon: <EnvelopeIcon className="h-4 w-4" />,
                action: () => onNavigate("mail"),
                keywords: "mail gateway email",
            },
            {
                id: "nav-ztna",
                label: "ZTNA",
                category: "Navigation",
                icon: <LockClosedIcon className="h-4 w-4" />,
                action: () => onNavigate("ztna"),
                keywords: "zero trust network access ztna",
            },
            {
                id: "nav-phishing",
                label: "Phishing Simulations",
                category: "Navigation",
                icon: <EnvelopeIcon className="h-4 w-4" />,
                action: () => onNavigate("phishing"),
                keywords: "phishing simulation campaign",
            },
            {
                id: "nav-pentests",
                label: "Pentests",
                category: "Navigation",
                icon: <BugAntIcon className="h-4 w-4" />,
                action: () => onNavigate("pentests"),
                keywords: "penetration testing pentests findings",
            },
            {
                id: "nav-tickets",
                label: "Support Tickets",
                category: "Navigation",
                icon: <TicketIcon className="h-4 w-4" />,
                action: () => onNavigate("tickets"),
                keywords: "tickets support help",
            },
            {
                id: "nav-chat",
                label: "Live Chat",
                category: "Navigation",
                icon: <ChatBubbleLeftRightIcon className="h-4 w-4" />,
                action: () => onNavigate("chat"),
                keywords: "chat support live",
            },
            {
                id: "nav-settings-notif",
                label: "Notification Settings",
                category: "Navigation",
                icon: <BellIcon className="h-4 w-4" />,
                action: () => onNavigate("settings-notifications"),
                keywords: "settings notifications alerts",
            },
            {
                id: "nav-settings-exc",
                label: "SOC Exceptions",
                category: "Navigation",
                icon: <ExclamationTriangleIcon className="h-4 w-4" />,
                action: () => onNavigate("settings-soc-exceptions"),
                keywords: "settings soc exceptions rules",
            },
            {
                id: "nav-profile",
                label: "Profile",
                category: "Navigation",
                icon: <UserCircleIcon className="h-4 w-4" />,
                action: () => onNavigate("settings-profile"),
                keywords: "profile account user settings",
            },
            {
                id: "settings-approved-software",
                label: "Approved Software",
                category: "Navigation",
                icon: <UserCircleIcon className="h-4 w-4" />,
                action: () => onNavigate("settings-approved-software"),
                keywords: "profile account user settings",
            },
        ];

        const events: CommandItem[] = UNIFIED_EVENTS.slice(0, 15).map((ev) => ({
            id: `event-${ev.id}`,
            label: `${ev.id} — ${ev.title}`,
            category: "Events",
            icon: <EyeIcon className="h-4 w-4" />,
            action: () => onInvestigate(ev),
            keywords: ev.searchableText,
        }));

        const actions: CommandItem[] = [
            {
                id: "action-refresh",
                label: "Refresh Data",
                category: "Actions",
                icon: <ArrowPathIcon className="h-4 w-4" />,
                action: () => {},
                keywords: "refresh reload data",
            },
            {
                id: "action-export",
                label: "Export Report",
                category: "Actions",
                icon: <CommandLineIcon className="h-4 w-4" />,
                action: () => {},
                keywords: "export report pdf csv",
            },
        ];

        return [...nav, ...events, ...actions];
    }, [onNavigate, onInvestigate]);

    const filtered = useMemo(() => {
        if (!query) {
            // Show recent views first, then rest
            const recentItems = recentViews
                .slice(0, 3)
                .map((v) => allItems.find((it) => it.id === `nav-${v}`))
                .filter(Boolean) as CommandItem[];
            const rest = allItems.filter((it) => !recentItems.includes(it)).slice(0, 12);
            return [...recentItems, ...rest];
        }
        const q = query.toLowerCase();
        return allItems.filter(
            (item) =>
                item.label.toLowerCase().includes(q) ||
                (item.keywords && item.keywords.toLowerCase().includes(q)) ||
                item.category.toLowerCase().includes(q),
        );
    }, [query, allItems, recentViews]);

    // Reset selection on filter change
    useEffect(() => {
        setSelectedIndex(0);
    }, [filtered.length, query]);

    // Focus input when opened
    useEffect(() => {
        if (open) {
            setQuery("");
            setSelectedIndex(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex((prev) => Math.max(prev - 1, 0));
            } else if (e.key === "Enter" && filtered[selectedIndex]) {
                e.preventDefault();
                filtered[selectedIndex].action();
                onClose();
            } else if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            }
        },
        [filtered, selectedIndex, onClose],
    );

    if (!open) return null;

    // Group filtered items by category
    const grouped = filtered.reduce(
        (acc, item) => {
            if (!acc[item.category]) acc[item.category] = [];
            acc[item.category].push(item);
            return acc;
        },
        {} as Record<string, CommandItem[]>,
    );

    let runningIndex = -1;

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh]">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-md" onClick={onClose} />

            {/* Modal */}
            <div
                className="relative w-full max-w-xl animate-[commandSlideIn_0.2s_ease-out] overflow-hidden rounded-2xl border border-white/15 bg-white/80 shadow-2xl shadow-black/30 backdrop-blur-2xl dark:border-zinc-700/50 dark:bg-zinc-900/90"
                onKeyDown={handleKeyDown}
            >
                {/* Search input */}
                <div className="flex items-center gap-3 border-b border-zinc-200/50 px-5 py-4 dark:border-zinc-700/50">
                    <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0 text-zinc-400 dark:text-zinc-500" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search commands, views, events..."
                        className="flex-1 bg-transparent text-sm text-zinc-900 placeholder-zinc-400 outline-none dark:text-white dark:placeholder-zinc-500"
                    />
                    <kbd className="rounded-md border border-zinc-200/50 bg-zinc-100/80 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400 dark:border-zinc-700/50 dark:bg-zinc-800/80 dark:text-zinc-500">
                        ESC
                    </kbd>
                </div>

                {/* Results */}
                <div className="max-h-[400px] overflow-y-auto py-2">
                    {filtered.length === 0 && (
                        <div className="px-5 py-8 text-center">
                            <InformationCircleIcon className="mx-auto h-8 w-8 text-zinc-300 dark:text-zinc-600" />
                            <p className="mt-2 text-sm text-zinc-400 dark:text-zinc-500">No results found</p>
                        </div>
                    )}

                    {Object.entries(grouped).map(([category, items]) => (
                        <div key={category}>
                            <div className="px-5 pb-1 pt-3">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 dark:text-zinc-500">
                                    {category}
                                    {!query && category === "Navigation" && recentViews.length > 0 && (
                                        <span className="ml-2 font-normal normal-case tracking-normal text-zinc-300 dark:text-zinc-600">
                                            — recently visited
                                        </span>
                                    )}
                                </span>
                            </div>
                            {items.map((item) => {
                                runningIndex++;
                                const idx = runningIndex;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => {
                                            item.action();
                                            onClose();
                                        }}
                                        onMouseEnter={() => setSelectedIndex(idx)}
                                        className={`flex w-full items-center gap-3 px-5 py-2.5 text-left transition-all duration-100 ${
                                            idx === selectedIndex
                                                ? "bg-blue-500/10 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400"
                                                : "text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800/50"
                                        }`}
                                    >
                                        <span
                                            className={`flex-shrink-0 ${idx === selectedIndex ? "text-blue-500" : "text-zinc-400 dark:text-zinc-500"}`}
                                        >
                                            {item.icon}
                                        </span>
                                        <span className="flex-1 truncate text-sm">{item.label}</span>
                                        {idx === selectedIndex && (
                                            <span className="text-[10px] text-zinc-400 dark:text-zinc-500">
                                                <kbd className="rounded border border-zinc-200/50 bg-zinc-100/80 px-1 py-0.5 dark:border-zinc-700/50 dark:bg-zinc-800/80">
                                                    ↵
                                                </kbd>
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-zinc-200/50 px-5 py-2.5 dark:border-zinc-700/50">
                    <div className="flex items-center gap-3 text-[10px] text-zinc-400 dark:text-zinc-500">
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border border-zinc-200/50 bg-zinc-100/80 px-1 py-0.5 dark:border-zinc-700/50 dark:bg-zinc-800/80">
                                ↑↓
                            </kbd>
                            Navigate
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border border-zinc-200/50 bg-zinc-100/80 px-1 py-0.5 dark:border-zinc-700/50 dark:bg-zinc-800/80">
                                ↵
                            </kbd>
                            Select
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border border-zinc-200/50 bg-zinc-100/80 px-1 py-0.5 dark:border-zinc-700/50 dark:bg-zinc-800/80">
                                esc
                            </kbd>
                            Close
                        </span>
                    </div>
                    <span className="text-[10px] text-zinc-300 dark:text-zinc-600">{filtered.length} results</span>
                </div>
            </div>

            <style>{`
                @keyframes commandSlideIn {
                    from { opacity: 0; transform: scale(0.97) translateY(-8px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────────────
   KEYBOARD SHORTCUTS HELP DIALOG
   ────────────────────────────────────────────────────────────────────────────── */

function KeyboardShortcutsHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
    if (!open) return null;

    const shortcuts = [
        { keys: ["⌘", "K"], description: "Open Command Palette" },
        { keys: ["Esc"], description: "Close panels & dialogs" },
        { keys: ["1"], description: "Overview Dashboard" },
        { keys: ["2"], description: "SOC Dashboard" },
        { keys: ["3"], description: "NDR Dashboard" },
        { keys: ["4"], description: "Mail Gateway" },
        { keys: ["5"], description: "ZTNA" },
        { keys: ["6"], description: "Tickets" },
        { keys: ["7"], description: "Live Chat" },
        { keys: ["?"], description: "Show this help" },
    ];

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-md" onClick={onClose} />
            <div className="relative w-full max-w-md animate-[commandSlideIn_0.2s_ease-out] overflow-hidden rounded-2xl border border-white/15 bg-white/80 shadow-2xl shadow-black/30 backdrop-blur-2xl dark:border-zinc-700/50 dark:bg-zinc-900/90">
                <div className="flex items-center justify-between border-b border-zinc-200/50 px-6 py-4 dark:border-zinc-700/50">
                    <div className="flex items-center gap-2">
                        <QuestionMarkCircleIcon className="h-5 w-5 text-zinc-400" />
                        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Keyboard Shortcuts</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
                <div className="max-h-[400px] overflow-y-auto p-4">
                    {shortcuts.map((s, i) => (
                        <div
                            key={i}
                            className="flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                        >
                            <span className="text-sm text-zinc-600 dark:text-zinc-400">{s.description}</span>
                            <div className="flex items-center gap-1">
                                {s.keys.map((key, j) => (
                                    <kbd
                                        key={j}
                                        className="min-w-[24px] rounded-md border border-zinc-200/60 bg-zinc-100/80 px-2 py-1 text-center text-[11px] font-semibold text-zinc-500 shadow-sm dark:border-zinc-700/50 dark:bg-zinc-800/80 dark:text-zinc-400"
                                    >
                                        {key}
                                    </kbd>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <style>{`
                    @keyframes commandSlideIn {
                        from { opacity: 0; transform: scale(0.97) translateY(-8px); }
                        to { opacity: 1; transform: scale(1) translateY(0); }
                    }
                `}</style>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────────────
   STATUS BAR COMPONENT
   ────────────────────────────────────────────────────────────────────────────── */

function StatusBar() {
    const [eventsPerSec, setEventsPerSec] = useState(142);
    const [lastSync, setLastSync] = useState("just now");

    useEffect(() => {
        const id = setInterval(() => {
            setEventsPerSec(Math.floor(120 + Math.random() * 60));
            setLastSync("just now");
        }, 5000);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="fixed bottom-0 left-0 right-0 z-30 flex h-7 items-center justify-between border-t border-zinc-200/40 bg-white/70 px-4 text-[11px] backdrop-blur-xl dark:border-zinc-800/60 dark:bg-zinc-950/70">
            {/* Left side */}
            <div className="flex items-center gap-4">
                {/* Connection */}
                <div className="flex items-center gap-1.5">
                    <WifiIcon className="h-3 w-3 text-emerald-500" />
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">Connected</span>
                </div>
                <div className="h-3 w-px bg-zinc-200 dark:bg-zinc-700" />
                {/* Last sync */}
                <div className="flex items-center gap-1.5 text-zinc-400 dark:text-zinc-500">
                    <ArrowPathIcon className="h-3 w-3" />
                    <span>Synced {lastSync}</span>
                </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
                {/* Events per second */}
                <div className="flex items-center gap-1.5 text-zinc-400 dark:text-zinc-500">
                    <BoltIcon className="h-3 w-3 text-amber-500" />
                    <span className="font-mono tabular-nums">{eventsPerSec}</span>
                    <span>events/sec</span>
                </div>
                <div className="h-3 w-px bg-zinc-200 dark:bg-zinc-700" />
                {/* System health */}
                <div className="flex items-center gap-1.5">
                    <CpuChipIcon className="h-3 w-3 text-emerald-500" />
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">All Systems Operational</span>
                </div>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────────────
   SIDEBAR BADGE COMPONENT
   ────────────────────────────────────────────────────────────────────────────── */

function SidebarBadge({ count, pulse }: { count: number; pulse?: boolean }) {
    return (
        <span className="relative ml-auto">
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 px-1.5 text-[10px] font-bold text-white shadow-md shadow-blue-500/25">
                {count}
            </span>
            {pulse && (
                <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-ping rounded-full bg-blue-400 opacity-60" />
            )}
        </span>
    );
}

function PulsingDot() {
    return (
        <span className="relative ml-auto flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-40" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/50" />
        </span>
    );
}

/* ──────────────────────────────────────────────────────────────────────────────
   MAIN LANDING PAGE COMPONENT
   ────────────────────────────────────────────────────────────────────────────── */

export default function LandingPage() {
    const { t, i18n } = useTranslation();
    const [viewCtx, setViewCtx] = useState<ViewContext>({ view: "overview" });
    const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
    const [investigateEvent, setInvestigateEvent] = useState<UnifiedEvent | null>(null);
    const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
    const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
    const [recentViews, setRecentViews] = useState<View[]>([]);
    const [transitioning, setTransitioning] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
    const prevViewRef = useRef<View>("overview");

    useEffect(() => {
        if (dark) {
            document.documentElement.classList.add("dark");
            localStorage.theme = "dark";
        } else {
            document.documentElement.classList.remove("dark");
            localStorage.theme = "light";
        }
    }, [dark]);

    const toggleLanguage = () => i18n.changeLanguage(i18n.language.startsWith("de") ? "en" : "de");

    const toggleSection = useCallback((section: string) => {
        setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    }, []);

    const handleNavigate = useCallback(
        (v: View) => {
            if (v === viewCtx.view) return;
            prevViewRef.current = viewCtx.view;

            // Trigger transition animation
            setTransitioning(true);
            setTimeout(() => {
                setViewCtx({ view: v });
                setInvestigateEvent(null);
                // Track recent views
                setRecentViews((prev) => {
                    const filtered = prev.filter((pv) => pv !== v);
                    return [v, ...filtered].slice(0, 5);
                });
                setTimeout(() => setTransitioning(false), 20);
            }, 150);
        },
        [viewCtx.view],
    );

    const handleNavigateDetail = useCallback((ctx: ViewContext) => {
        setTransitioning(true);
        setTimeout(() => {
            setViewCtx(ctx);
            setInvestigateEvent(null);
            setTimeout(() => setTransitioning(false), 20);
        }, 150);
    }, []);

    // Global keyboard shortcuts
    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            // Cmd+K or Ctrl+K — Command Palette
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                setCommandPaletteOpen((prev) => !prev);
                return;
            }

            // Don't trigger shortcuts when typing in inputs
            const target = e.target as HTMLElement;
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;

            // Escape — close things
            if (e.key === "Escape") {
                if (commandPaletteOpen) {
                    setCommandPaletteOpen(false);
                } else if (shortcutsHelpOpen) {
                    setShortcutsHelpOpen(false);
                } else if (investigateEvent) {
                    setInvestigateEvent(null);
                }
                return;
            }

            // ? — Show keyboard shortcuts
            if (e.key === "?") {
                setShortcutsHelpOpen((prev) => !prev);
                return;
            }

            // Number keys for quick nav
            const shortcut = VIEW_SHORTCUTS.find((s) => s.key === e.key);
            if (shortcut && !e.metaKey && !e.ctrlKey && !e.altKey) {
                handleNavigate(shortcut.view);
            }
        }

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [commandPaletteOpen, shortcutsHelpOpen, investigateEvent, handleNavigate]);

    const view = viewCtx.view;

    /* ── Sidebar ─────────────────────────────────────────────────────────── */

    const sidebarContent = (
        <Sidebar>
            <SidebarHeader>
                <SidebarSection>
                    <SidebarItem>
                        <img src="/logo.svg" alt="Trufflepig" className="h-8 w-8" />
                        <span className="text-base font-bold text-zinc-950 dark:text-white">
                            Trufflepig{" "}
                            <span className="bg-gradient-to-r from-zinc-500 to-zinc-400 bg-clip-text font-normal text-transparent dark:from-zinc-400 dark:to-zinc-500">
                                Platform
                            </span>
                        </span>
                    </SidebarItem>
                </SidebarSection>
            </SidebarHeader>
            <SidebarBody>
                <SidebarSection>
                    <SidebarItem current={view === "overview"} onClick={() => handleNavigate("overview")}>
                        <ChartBarIcon data-slot="icon" />
                        <SidebarLabel>{t("sidebar.overview")}</SidebarLabel>
                    </SidebarItem>
                    <Button color={"red"} className={"justify-start!"}>
                        <BellIcon data-slot="icon"/>
                        <SidebarLabel>Emergency Request</SidebarLabel>
                    </Button>
                </SidebarSection>

                {/* Monitoring — collapsible */}
                <SidebarSection>
                    <button onClick={() => toggleSection("monitoring")} className="flex w-full items-center gap-1">
                        <SidebarHeading>{t("sidebar.monitoring")}</SidebarHeading>
                        <ChevronRightIcon
                            className={`ml-auto h-3.5 w-3.5 text-zinc-400 transition-transform duration-200 dark:text-zinc-500 ${
                                collapsedSections["monitoring"] ? "" : "rotate-90"
                            }`}
                        />
                    </button>
                    <div
                        className={`overflow-hidden transition-all duration-300 ${
                            collapsedSections["monitoring"] ? "max-h-0 opacity-0" : "max-h-40 opacity-100"
                        }`}
                    >
                        <SidebarItem
                            current={view === "soc" || view === "soc-alert-detail"}
                            onClick={() => handleNavigate("soc")}
                        >
                            <ShieldCheckIcon data-slot="icon" />
                            <SidebarLabel>{t("sidebar.soc")}</SidebarLabel>
                        </SidebarItem>
                        <SidebarItem current={view === "ndr"} onClick={() => handleNavigate("ndr")}>
                            <SignalIcon data-slot="icon" />
                            <SidebarLabel>{t("sidebar.ndr")}</SidebarLabel>
                        </SidebarItem>
                    </div>
                </SidebarSection>

                {/* Protection — collapsible */}
                <SidebarSection>
                    <button onClick={() => toggleSection("protection")} className="flex w-full items-center gap-1">
                        <SidebarHeading>{t("sidebar.protection")}</SidebarHeading>
                        <ChevronRightIcon
                            className={`ml-auto h-3.5 w-3.5 text-zinc-400 transition-transform duration-200 dark:text-zinc-500 ${
                                collapsedSections["protection"] ? "" : "rotate-90"
                            }`}
                        />
                    </button>
                    <div
                        className={`overflow-hidden transition-all duration-300 ${
                            collapsedSections["protection"] ? "max-h-0 opacity-0" : "max-h-40 opacity-100"
                        }`}
                    >
                        <SidebarItem current={view === "mail"} onClick={() => handleNavigate("mail")}>
                            <EnvelopeIcon data-slot="icon" />
                            <SidebarLabel>{t("sidebar.mailGateway")}</SidebarLabel>
                        </SidebarItem>
                        <SidebarItem current={view === "ztna"} onClick={() => handleNavigate("ztna")}>
                            <LockClosedIcon data-slot="icon" />
                            <SidebarLabel>{t("sidebar.ztna")}</SidebarLabel>
                        </SidebarItem>
                    </div>
                </SidebarSection>

                {/* Assessment — collapsible */}
                <SidebarSection>
                    <button onClick={() => toggleSection("assessment")} className="flex w-full items-center gap-1">
                        <SidebarHeading>{t("sidebar.assessment")}</SidebarHeading>
                        <ChevronRightIcon
                            className={`ml-auto h-3.5 w-3.5 text-zinc-400 transition-transform duration-200 dark:text-zinc-500 ${
                                collapsedSections["assessment"] ? "" : "rotate-90"
                            }`}
                        />
                    </button>
                    <div
                        className={`overflow-hidden transition-all duration-300 ${
                            collapsedSections["assessment"] ? "max-h-0 opacity-0" : "max-h-40 opacity-100"
                        }`}
                    >
                        <SidebarItem current={view === "phishing"} onClick={() => handleNavigate("phishing")}>
                            <EnvelopeIcon data-slot="icon" />
                            <SidebarLabel>{t("sidebar.phishing")}</SidebarLabel>
                        </SidebarItem>
                        <SidebarItem
                            current={view === "pentests" || view === "pentest-finding-detail"}
                            onClick={() => handleNavigate("pentests")}
                        >
                            <BugAntIcon data-slot="icon" />
                            <SidebarLabel>{t("sidebar.pentests")}</SidebarLabel>
                        </SidebarItem>
                    </div>
                </SidebarSection>

                <SidebarDivider />

                {/* Files — collapsible */}
                <SidebarSection>
                    <button onClick={() => toggleSection("files")} className="flex w-full items-center gap-1">
                        <SidebarHeading>{t("sidebar.files")}</SidebarHeading>
                        <ChevronRightIcon
                            className={`ml-auto h-3.5 w-3.5 text-zinc-400 transition-transform duration-200 dark:text-zinc-500 ${
                                collapsedSections["files"] ? "" : "rotate-90"
                            }`}
                        />
                    </button>
                    <div
                        className={`overflow-hidden transition-all duration-300 ${
                            collapsedSections["files"] ? "max-h-0 opacity-0" : "max-h-40 opacity-100"
                        }`}
                    >
                        <SidebarItem current={view === "contracts"} onClick={() => handleNavigate("contracts")}>
                            <ArchiveBoxIcon data-slot="icon" />
                            <SidebarLabel>Contracts</SidebarLabel>{" "}
                            {/* TODO Show active and expired customer contracts */}
                        </SidebarItem>
                        <SidebarItem current={view === "downloads"} onClick={() => handleNavigate("downloads")}>
                            {" "}
                            {/*TODO Downloads such as SentinelOne including setup steps*/}
                            <ArchiveBoxArrowDownIcon data-slot="icon" />
                            <SidebarLabel>{t("sidebar.downloads")}</SidebarLabel>
                        </SidebarItem>
                        <SidebarItem current={view === "archive"} onClick={() => handleNavigate("archive")}>
                            {" "}
                            {/*TODO File archive for old reports, incident data etc. Include a date when this data will be removed from our system (ISMS)*/}
                            <ArchiveBoxIcon data-slot="icon" />
                            <SidebarLabel>{t("sidebar.archive")}</SidebarLabel>
                        </SidebarItem>
                    </div>
                </SidebarSection>

                <SidebarDivider />

                {/* Support — with badges */}
                <SidebarSection>
                    <SidebarHeading>{t("sidebar.support")}</SidebarHeading>
                    <SidebarItem
                        current={view === "tickets" || view === "ticket-detail"}
                        onClick={() => handleNavigate("tickets")}
                    >
                        <TicketIcon data-slot="icon" />
                        <SidebarLabel>{t("sidebar.tickets")}</SidebarLabel>
                        <SidebarBadge count={3} />
                    </SidebarItem>
                    <SidebarItem current={view === "chat"} onClick={() => handleNavigate("chat")}>
                        <ChatBubbleLeftRightIcon data-slot="icon" />
                        <SidebarLabel>{t("sidebar.chat")}</SidebarLabel>
                        <PulsingDot />
                    </SidebarItem>
                </SidebarSection>

                <SidebarDivider />

                {/* Settings */}
                <SidebarSection>
                    <SidebarHeading>{t("sidebar.settings")}</SidebarHeading>
                    <SidebarItem
                        current={view === "settings-notifications"}
                        onClick={() => handleNavigate("settings-notifications")}
                    >
                        <BellIcon data-slot="icon" />
                        <SidebarLabel>{t("sidebar.notifications")}</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem
                        current={view === "settings-soc-exceptions"}
                        onClick={() => handleNavigate("settings-soc-exceptions")}
                    >
                        <ExclamationTriangleIcon data-slot="icon" />
                        <SidebarLabel>{t("sidebar.socExceptions")}</SidebarLabel>{" "}
                        {/*TODO rename to SOC Settings -> Allow uploading of customer image to sandbox, show exclusions, USB, Bluetooth, Thunderbolt Control*/}
                    </SidebarItem>
                    <SidebarItem>
                        <EnvelopeIcon data-slot="icon" />
                        <SidebarLabel>Mail Security Settings</SidebarLabel>{" "}
                        {/*TODO Settings such as banner and mail templates (toggle on/off, edit) also phishing campaigns (trufflepig toggle, other request via support button)*/}
                    </SidebarItem>
                    <SidebarItem
                        current={view === "settings-approved-software"}
                        onClick={() => handleNavigate("settings-approved-software")}
                    >
                        <BriefcaseIcon data-slot="icon" />
                        <SidebarLabel>Approved Software</SidebarLabel>{" "}
                        {/*TODO List detected and installed software and allow to "whitelist" it*/}
                    </SidebarItem>
                    <SidebarItem
                        current={view === "settings-profile"}
                        onClick={() => handleNavigate("settings-profile")}
                    >
                        <UserCircleIcon data-slot="icon" />
                        <SidebarLabel>{t("sidebar.profile")}</SidebarLabel>
                    </SidebarItem>
                </SidebarSection>

                <SidebarSpacer />
                <SidebarDivider />

                <SidebarSection>
                    <SidebarItem onClick={toggleLanguage}>
                        <GlobeAltIcon data-slot="icon" />
                        <SidebarLabel>{i18n.language.startsWith("de") ? "English" : "Deutsch"}</SidebarLabel>
                    </SidebarItem>
                    <SidebarItem onClick={() => setDark(!dark)}>
                        {dark ? <SunIcon data-slot="icon" /> : <MoonIcon data-slot="icon" />}
                        <SidebarLabel>{dark ? t("theme.light") : t("theme.dark")}</SidebarLabel>
                    </SidebarItem>
                </SidebarSection>

                {/* Sidebar footer - system status */}
                <div className="mt-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5">
                    <div className="flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                            All Systems Operational
                        </span>
                    </div>
                </div>
            </SidebarBody>
        </Sidebar>
    );

    /* ── Navbar ───────────────────────────────────────────────────────────── */

    const navbarContent = (
        <Navbar>
            {/* Command palette trigger */}
            <NavbarSection>
                <button
                    onClick={() => setCommandPaletteOpen(true)}
                    className="flex items-center gap-2 rounded-xl border border-zinc-200/40 bg-zinc-50/60 px-3 py-1.5 text-xs text-zinc-400 transition-all duration-200 hover:border-zinc-300/60 hover:bg-zinc-100/80 hover:text-zinc-500 dark:border-zinc-700/40 dark:bg-zinc-800/40 dark:text-zinc-500 dark:hover:border-zinc-600/60 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-400"
                >
                    <MagnifyingGlassIcon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Search...</span>
                    <kbd className="hidden rounded-md border border-zinc-200/50 bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-300 shadow-sm sm:inline dark:border-zinc-600/50 dark:bg-zinc-700/80 dark:text-zinc-500">
                        ⌘K
                    </kbd>
                </button>
            </NavbarSection>

            <NavbarSpacer />

            <NavbarSection>
                <div className="flex items-center gap-2">
                    <SearchBar onNavigate={handleNavigate} onInvestigate={setInvestigateEvent} />

                    {/* Separator */}
                    <div className="mx-1 hidden h-6 w-px bg-zinc-200/60 lg:block dark:bg-zinc-700/60" />

                    {/* Live Clock */}
                    <LiveClock />

                    {/* Separator */}
                    <div className="mx-1 hidden h-6 w-px bg-zinc-200/60 lg:block dark:bg-zinc-700/60" />

                    {/* Notification Bell */}
                    <NotificationBell />

                    {/* User Avatar Menu */}
                    <UserAvatarMenu onNavigate={handleNavigate} />
                </div>
            </NavbarSection>
        </Navbar>
    );

    /* ── Main Content ─────────────────────────────────────────────────────── */

    return (
        <>
            <SidebarLayout sidebar={sidebarContent} navbar={navbarContent}>
                {/* Breadcrumbs */}
                <Breadcrumbs viewCtx={viewCtx} onNavigate={handleNavigate} />

                {/* View content with transition */}
                <div
                    className={`min-h-full pb-8 transition-all duration-200 ease-out ${
                        transitioning ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"
                    }`}
                >
                    {view === "overview" && (
                        <OverviewDashboard onNavigate={handleNavigate} onInvestigate={setInvestigateEvent} />
                    )}
                    {view === "soc" && (
                        <SocDashboard
                            onNavigate={handleNavigate}
                            onInvestigate={setInvestigateEvent}
                            onNavigateDetail={handleNavigateDetail}
                        />
                    )}
                    {view === "ndr" && <NdrDashboard onNavigate={handleNavigate} onInvestigate={setInvestigateEvent} />}
                    {view === "mail" && (
                        <MailDashboard onNavigate={handleNavigate} onInvestigate={setInvestigateEvent} />
                    )}
                    {view === "archive" && <ArchiveDashboard />}
                    {view === "contracts" && <ContractDashboard />}
                    {view === "downloads" && <DownloadsDashboard />}
                    {view === "ztna" && (
                        <ZtnaDashboard onNavigate={handleNavigate} onInvestigate={setInvestigateEvent} />
                    )}
                    {view === "phishing" && (
                        <PhishingDashboard onNavigate={handleNavigate} onInvestigate={setInvestigateEvent} />
                    )}
                    {view === "pentests" && (
                        <PentestsDashboard
                            onNavigate={handleNavigate}
                            onInvestigate={setInvestigateEvent}
                            onNavigateDetail={handleNavigateDetail}
                        />
                    )}
                    {view === "soc-alert-detail" && (
                        <SocAlertDetailView
                            eventId={viewCtx.detailId ?? ""}
                            onNavigate={handleNavigate}
                            onNavigateDetail={handleNavigateDetail}
                        />
                    )}
                    {view === "pentest-finding-detail" && (
                        <PentestFindingDetailView
                            findingId={viewCtx.detailId ?? ""}
                            onNavigate={handleNavigate}
                            onNavigateDetail={handleNavigateDetail}
                        />
                    )}
                    {view === "tickets" && <TicketListView onNavigateDetail={handleNavigateDetail} />}
                    {view === "ticket-detail" && (
                        <TicketDetailView
                            ticketId={viewCtx.detailId ?? ""}
                            onNavigate={handleNavigate}
                            onNavigateDetail={handleNavigateDetail}
                        />
                    )}
                    {view === "chat" && <ChatView />}
                    {view === "settings-notifications" && <SettingsNotifications />}
                    {view === "settings-soc-exceptions" && <SettingsSocExceptions />}
                    {view === "settings-profile" && <SettingsProfile />}
                    {view === "settings-approved-software" && <SoftwareInventory />}
                </div>

                {/* Investigate Panel overlay */}
                {investigateEvent && (
                    <>
                        <div
                            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-md"
                            onClick={() => setInvestigateEvent(null)}
                        />
                        <InvestigatePanel
                            event={investigateEvent}
                            onClose={() => setInvestigateEvent(null)}
                            onNavigate={handleNavigate}
                        />
                    </>
                )}
            </SidebarLayout>

            {/* Status Bar */}
            <StatusBar />

            {/* Command Palette */}
            <CommandPalette
                open={commandPaletteOpen}
                onClose={() => setCommandPaletteOpen(false)}
                onNavigate={handleNavigate}
                onInvestigate={setInvestigateEvent}
                recentViews={recentViews}
            />

            {/* Keyboard Shortcuts Help */}
            <KeyboardShortcutsHelp open={shortcutsHelpOpen} onClose={() => setShortcutsHelpOpen(false)} />
        </>
    );
}
